import { CustomerStatusDto } from '@/api/generated';

/** The statuses a person holding CUSTOMER_APPROVE may choose when creating a customer; Prospekt first. */
const CREATE_STATUSES: CustomerStatusDto[] = [CustomerStatusDto.Prospect, CustomerStatusDto.Active];

/** The statuses the customer edit switches between; Prospekt and Odrzucony change only by a decision. */
const EDIT_STATUSES: CustomerStatusDto[] = [CustomerStatusDto.Active, CustomerStatusDto.Inactive];

export type CustomerStatusFieldModel = {
  /** The status preselected in the form; null when the field is hidden (the backend creates a Prospekt). */
  defaultStatus: CustomerStatusDto | null;
  /** The status is shown but cannot be changed (Prospekt or Odrzucony klient in the edit). */
  isLocked: boolean;
  isVisible: boolean;
  statuses: CustomerStatusDto[];
};

/** The key of the reason, under `views.customers.status.processPicker`, why a customer cannot be a process customer. */
export type ProcessCustomerBlockedReasonKey = 'inactive' | 'requiresApproval';

export const customerStatusUtil = {
  /** Only an ACTIVE customer can become the customer of a process (Zatwierdzenie klienta). */
  canBeProcessCustomer: (status: CustomerStatusDto | undefined): boolean =>
    status === undefined || status === CustomerStatusDto.Active,
  /**
   * Why a customer cannot become the customer of a process: a Prospekt or an Odrzucony klient waits for
   * the approval, any other blocked customer is inactive.
   */
  processCustomerBlockedReason: (
    status: CustomerStatusDto | undefined,
  ): ProcessCustomerBlockedReasonKey =>
    customerStatusUtil.requiresApproval(status) ? 'requiresApproval' : 'inactive',
  /** Prospekt or Odrzucony klient: waits for the approval decision. */
  requiresApproval: (status: CustomerStatusDto | undefined): boolean =>
    status === CustomerStatusDto.Prospect || status === CustomerStatusDto.Rejected,
  /**
   * The status field of the customer form. Creating: only a person holding CUSTOMER_APPROVE sees it,
   * with Prospekt (default) and Aktywny. Editing: ACTIVE and INACTIVE switch freely, Prospekt and
   * Odrzucony klient are shown locked.
   */
  resolveStatusField: (params: {
    canApprove: boolean;
    currentStatus?: CustomerStatusDto;
  }): CustomerStatusFieldModel => {
    const { canApprove, currentStatus } = params;
    if (!currentStatus) {
      return canApprove
        ? {
            defaultStatus: CustomerStatusDto.Prospect,
            isLocked: false,
            isVisible: true,
            statuses: CREATE_STATUSES,
          }
        : { defaultStatus: null, isLocked: false, isVisible: false, statuses: [] };
    }
    if (customerStatusUtil.requiresApproval(currentStatus)) {
      return {
        defaultStatus: currentStatus,
        isLocked: true,
        isVisible: true,
        statuses: [currentStatus],
      };
    }
    return {
      defaultStatus: currentStatus,
      isLocked: false,
      isVisible: true,
      statuses: EDIT_STATUSES,
    };
  },
};
